import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
    Wine, 
    Plus, 
    Pencil, 
    Trash2, 
    Save, 
    X, 
    Upload,
    Image as ImageIcon,
    RefreshCw,
    Eye,
    EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

// ============================================================
// PAGE D'ADMINISTRATION DES COCKTAILS
// Permet de modifier les images et descriptions des cocktails
// ============================================================

const BRAND_GREEN = '#1a3d2c';

const COCKTAIL_CATEGORIES = [
    { id: 'signature', name: 'Signature', color: '#7c3aed' },
    { id: 'fraicheur', name: 'Fraîcheur', color: '#0ea5e9' },
    { id: 'classiques', name: 'Classiques', color: '#f59e0b' }
];

const CocktailsAdminPage = () => {
    const { api } = useAuth();
    const [cocktails, setCocktails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCocktail, setEditingCocktail] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        category: 'classiques',
        image_url: '',
        is_available: true
    });
    
    const fetchCocktails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api().get('/cocktails/admin/cocktails');
            setCocktails(response.data);
        } catch (error) {
            console.error('Error fetching cocktails:', error);
            toast.error('Erreur lors du chargement des cocktails');
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => {
        fetchCocktails();
    }, [fetchCocktails]);
    
    const openCreateDialog = () => {
        setEditingCocktail(null);
        setFormData({
            name: '',
            description: '',
            price: '',
            category: 'classiques',
            image_url: '',
            is_available: true
        });
        setIsDialogOpen(true);
    };
    
    const openEditDialog = (cocktail) => {
        setEditingCocktail(cocktail);
        setFormData({
            name: cocktail.name,
            description: cocktail.description,
            price: cocktail.price.toString(),
            category: cocktail.category,
            image_url: cocktail.image_url || '',
            is_available: cocktail.is_available
        });
        setIsDialogOpen(true);
    };
    
    const handleSave = async () => {
        if (!formData.name || !formData.description || !formData.price) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        
        setSaving(true);
        try {
            const payload = {
                ...formData,
                price: parseInt(formData.price)
            };
            
            if (editingCocktail) {
                await api().put(`/cocktails/admin/cocktails/${editingCocktail.id}`, payload);
                toast.success('Cocktail modifié avec succès');
            } else {
                await api().post('/cocktails/admin/cocktails', payload);
                toast.success('Cocktail créé avec succès');
            }
            
            setIsDialogOpen(false);
            fetchCocktails();
        } catch (error) {
            console.error('Error saving cocktail:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };
    
    const handleDelete = async (cocktailId) => {
        if (!window.confirm('Supprimer ce cocktail ?')) return;
        
        try {
            await api().delete(`/cocktails/admin/cocktails/${cocktailId}`);
            toast.success('Cocktail supprimé');
            fetchCocktails();
        } catch (error) {
            console.error('Error deleting cocktail:', error);
            toast.error('Erreur lors de la suppression');
        }
    };
    
    const handleToggleAvailability = async (cocktail) => {
        try {
            await api().put(`/cocktails/admin/cocktails/${cocktail.id}`, {
                is_available: !cocktail.is_available
            });
            toast.success(cocktail.is_available ? 'Cocktail masqué' : 'Cocktail visible');
            fetchCocktails();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    const handleResetDefaults = async () => {
        if (!window.confirm('Réinitialiser tous les cocktails aux valeurs par défaut ? Cette action est irréversible.')) return;
        
        try {
            await api().post('/cocktails/admin/cocktails/reset-defaults');
            toast.success('Cocktails réinitialisés');
            fetchCocktails();
        } catch (error) {
            toast.error('Erreur lors de la réinitialisation');
        }
    };
    
    const getCategoryColor = (categoryId) => {
        return COCKTAIL_CATEGORIES.find(c => c.id === categoryId)?.color || '#666';
    };
    
    const getCategoryName = (categoryId) => {
        return COCKTAIL_CATEGORIES.find(c => c.id === categoryId)?.name || categoryId;
    };
    
    const groupedCocktails = COCKTAIL_CATEGORIES.map(cat => ({
        ...cat,
        items: cocktails.filter(c => c.category === cat.id)
    }));
    
    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: BRAND_GREEN }}>
                        <Wine className="w-7 h-7" />
                        Gestion des Cocktails
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Modifiez les images et descriptions des cocktails affichés sur le site client
                    </p>
                </div>
                
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={handleResetDefaults}
                        className="flex items-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Réinitialiser
                    </Button>
                    <Button 
                        onClick={openCreateDialog}
                        className="flex items-center gap-2"
                        style={{ backgroundColor: BRAND_GREEN }}
                    >
                        <Plus className="w-4 h-4" />
                        Nouveau cocktail
                    </Button>
                </div>
            </div>
            
            {/* Lien vers le site client */}
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                <p className="text-sm text-purple-800">
                    <strong>Voir le résultat :</strong>{' '}
                    <a 
                        href="/client/cocktails" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-purple-600"
                    >
                        Ouvrir la page Cocktails du site client →
                    </a>
                </p>
            </div>
            
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <Card key={i} className="animate-pulse">
                            <div className="aspect-video bg-gray-200" />
                            <CardContent className="p-4 space-y-3">
                                <div className="h-5 bg-gray-200 rounded w-3/4" />
                                <div className="h-4 bg-gray-200 rounded w-1/2" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="space-y-8">
                    {groupedCocktails.map(category => (
                        <div key={category.id}>
                            <h2 
                                className="text-lg font-semibold mb-4 flex items-center gap-2"
                                style={{ color: category.color }}
                            >
                                <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                                <span className="text-gray-400 text-sm font-normal">
                                    ({category.items.length} cocktail{category.items.length > 1 ? 's' : ''})
                                </span>
                            </h2>
                            
                            {category.items.length === 0 ? (
                                <p className="text-gray-400 text-sm italic mb-4">
                                    Aucun cocktail dans cette catégorie
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {category.items.map(cocktail => (
                                        <Card 
                                            key={cocktail.id} 
                                            className={`overflow-hidden transition-all hover:shadow-lg ${!cocktail.is_available ? 'opacity-60' : ''}`}
                                        >
                                            {/* Image */}
                                            <div className="aspect-square relative bg-gray-100">
                                                {cocktail.image_url ? (
                                                    <img 
                                                        src={cocktail.image_url} 
                                                        alt={cocktail.name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <ImageIcon className="w-12 h-12 text-gray-300" />
                                                    </div>
                                                )}
                                                
                                                {/* Badge disponibilité */}
                                                {!cocktail.is_available && (
                                                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                                                        Masqué
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Content */}
                                            <CardContent className="p-4">
                                                <h3 className="font-semibold text-lg mb-1">{cocktail.name}</h3>
                                                <p className="text-gray-500 text-sm mb-2 line-clamp-2">
                                                    {cocktail.description}
                                                </p>
                                                <p className="font-bold text-lg" style={{ color: BRAND_GREEN }}>
                                                    {cocktail.price?.toLocaleString('fr-FR')} F
                                                </p>
                                                
                                                {/* Actions */}
                                                <div className="flex gap-2 mt-4 pt-3 border-t">
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => openEditDialog(cocktail)}
                                                        className="flex-1"
                                                    >
                                                        <Pencil className="w-3 h-3 mr-1" />
                                                        Modifier
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleToggleAvailability(cocktail)}
                                                    >
                                                        {cocktail.is_available ? (
                                                            <EyeOff className="w-4 h-4" />
                                                        ) : (
                                                            <Eye className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDelete(cocktail.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            
            {/* Dialog Edit/Create */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wine className="w-5 h-5" style={{ color: BRAND_GREEN }} />
                            {editingCocktail ? 'Modifier le cocktail' : 'Nouveau cocktail'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingCocktail 
                                ? 'Modifiez les informations du cocktail' 
                                : 'Ajoutez un nouveau cocktail à la carte'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Image Preview */}
                        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
                            {formData.image_url ? (
                                <img 
                                    src={formData.image_url} 
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                    <ImageIcon className="w-12 h-12 mb-2" />
                                    <span className="text-sm">Aperçu de l'image</span>
                                </div>
                            )}
                        </div>
                        
                        {/* Image URL */}
                        <div className="space-y-2">
                            <Label htmlFor="image_url">URL de l'image</Label>
                            <Input
                                id="image_url"
                                value={formData.image_url}
                                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                                placeholder="https://..."
                            />
                            <p className="text-xs text-gray-500">
                                Collez l'URL d'une image (Unsplash, Google Photos, etc.)
                            </p>
                        </div>
                        
                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom du cocktail *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Ex: Tropical Bissap"
                            />
                        </div>
                        
                        {/* Description */}
                        <div className="space-y-2">
                            <Label htmlFor="description">Description / Ingrédients *</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Ex: Bissap, Ananas, Citron"
                            />
                        </div>
                        
                        {/* Price & Category */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="price">Prix (FCFA) *</Label>
                                <Input
                                    id="price"
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                                    placeholder="2000"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Catégorie</Label>
                                <Select 
                                    value={formData.category}
                                    onValueChange={(value) => setFormData({...formData, category: value})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {COCKTAIL_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>
                                                <span className="flex items-center gap-2">
                                                    <div 
                                                        className="w-2 h-2 rounded-full"
                                                        style={{ backgroundColor: cat.color }}
                                                    />
                                                    {cat.name}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {/* Visibility toggle */}
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                id="is_available"
                                checked={formData.is_available}
                                onChange={(e) => setFormData({...formData, is_available: e.target.checked})}
                                className="w-4 h-4 rounded"
                            />
                            <Label htmlFor="is_available" className="cursor-pointer">
                                Visible sur le site client
                            </Label>
                        </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                            <X className="w-4 h-4 mr-1" />
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSave} 
                            disabled={saving}
                            style={{ backgroundColor: BRAND_GREEN }}
                        >
                            <Save className="w-4 h-4 mr-1" />
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CocktailsAdminPage;
